// Describes what Right Pane can display

import type { Time } from "@suse-o11y/date-time/time";
import { isArray } from "@suse-o11y/is-array";
import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { isNumber } from "@suse-o11y/is-number";
import { isPresent } from "@suse-o11y/is-present";
import { Logger } from "@suse-o11y/logger";
import isEmpty from "lodash/isEmpty";
import {
	type QueryParamConfig,
	decodeBoolean,
	decodeDelimitedArray,
	decodeEnum,
	decodeNumber,
	decodeObject,
	decodeString,
	encodeBoolean,
	encodeDelimitedArray,
	encodeNumber,
	encodeObject,
	encodeString,
} from "serialize-query-params";

import { decodeTimeNumber, encodeTimeNumber } from "../../../generic/Util";

const RIGHT_PANE_MAX_CHAIN_SIZE = 100;
const RIGHT_PANE_ITEM_FRAGMENTS_SEPARATOR = ":";
const RIGHT_PANE_ITEMS_SEPARATOR = ",";
const RIGHT_PANE_OBJECT_KEY_VALUE_SEPARATOR = "-";
const RIGHT_PANE_OBJECT_ENTRY_SEPARATOR = "__";

const logger = Logger.namespace("RightPaneState");

/**
 * -----------
 * E N C O D E
 * -----------
 */

const encodeRightPaneItem = (paneItem: RightPaneItem): string => {
	const fragments = [encodeString(paneItem.type)];

	if (paneItem.type === "event") {
		fragments.push(encodeString(paneItem.id), encodeNumber(paneItem.itemTime));
	} else if (paneItem.type === "span") {
		fragments.push(encodeString(paneItem.traceId), encodeString(paneItem.primarySpanId), encodeString(paneItem.spanId));
	} else if (paneItem.type === "span-event") {
		fragments.push(
			encodeString(paneItem.traceId),
			encodeString(paneItem.primarySpanId),
			encodeString(paneItem.spanId),
			encodeNumber(paneItem.index),
		);
	} else if (paneItem.type === "events") {
		fragments.push(
			encodeTimeNumber(paneItem.startTime),
			encodeTimeNumber(paneItem.endTime),
			encodeString(paneItem.eventType),
		);
	} else {
		fragments.push(isNumber(paneItem.id) ? encodeNumber(paneItem.id) : encodeString(paneItem.id));
	}

	return encodeDelimitedArray(fragments.filter(isPresent), RIGHT_PANE_ITEM_FRAGMENTS_SEPARATOR)!;
};

const encodeRightPane = (rightPane: undefined | null | RightPaneState): undefined | null | string => {
	// always return undefined if the value the same as RightPaneState.defaults
	if (rightPane == null || RightPaneState.isDefaults(rightPane)) {
		return undefined;
	}

	const shape: {
		hidden?: null | string;
		active_tab?: null | string;
		chain?: null | string;
	} = {};

	if (rightPane.hidden !== RightPaneState.defaults.hidden) {
		shape.hidden = encodeBoolean(rightPane.hidden);
	}

	if (rightPane.activeTab !== RightPaneState.defaults.activeTab) {
		shape.active_tab = encodeString(rightPane.activeTab);
	}

	if (rightPane.chain.length > 0) {
		shape.chain = encodeDelimitedArray(rightPane.chain.map(encodeRightPaneItem), RIGHT_PANE_ITEMS_SEPARATOR);
	}

	if (isEmpty(shape)) {
		return undefined;
	}

	return encodeObject(shape, RIGHT_PANE_OBJECT_KEY_VALUE_SEPARATOR, RIGHT_PANE_OBJECT_ENTRY_SEPARATOR);
};

/**
 * -----------
 * D E C O D E
 * -----------
 */

const decodeRightPaneActiveTab = (input: undefined | string): null | undefined | RightPaneActiveTab =>
	decodeEnum(input, ["pinned-items", "selection"]);

const decodeRightPaneItemType = (input: undefined | string): null | undefined | RightPaneItem["type"] =>
	decodeEnum(input, [
		"component",
		"relation",
		"group",
		"indirect-relation",
		"group-relation",
		"event",
		"events",
		"span",
		"span-event",
	]);

const decodeRightPaneItem = (input: string): null | RightPaneItem => {
	const rawFragments = decodeDelimitedArray(input, RIGHT_PANE_ITEM_FRAGMENTS_SEPARATOR);
	const fragments = isArray(rawFragments) ? rawFragments.filter(isPresent) : [];

	if (fragments.length < 2) {
		return null;
	}

	const itemType = decodeRightPaneItemType(fragments[0]);

	switch (itemType) {
		case null:
		case undefined: {
			logger.warn(`Unknown east pane fragment type: ${itemType}`, fragments);
			return null;
		}

		case "event": {
			const rawId = decodeString(fragments[1]);
			const rawItemTime = decodeNumber(fragments[2]);

			if (rawId == null || rawItemTime == null || isNaN(rawItemTime)) {
				logger.warn(`Missing URL fragments for east pane (${itemType})`, fragments);
				return null;
			}

			return { type: "event", id: rawId, itemTime: rawItemTime };
		}

		case "events": {
			const startTime = decodeTimeNumber(fragments[1]);
			const endTime = decodeTimeNumber(fragments[2]);
			const eventType = decodeString(fragments[3]);

			if (startTime == null || endTime == null) {
				logger.warn(`Missing URL fragments for east pane (${itemType})`, fragments);
				return null;
			}

			return { type: "events", startTime, endTime, eventType: eventType ?? undefined };
		}

		case "span": {
			const traceId = decodeString(fragments[1]);
			const rootSpanId = decodeString(fragments[2]);
			const spanId = decodeString(fragments[3]);

			if (traceId == null || spanId == null || rootSpanId == null) {
				logger.warn(`Missing URL fragments for east pane (${itemType})`, fragments);
				return null;
			}

			return { type: "span", traceId, primarySpanId: rootSpanId, spanId };
		}

		case "span-event": {
			const traceId = decodeString(fragments[1]);
			const rootSpanId = decodeString(fragments[2]);
			const spanId = decodeString(fragments[3]);
			const index = decodeNumber(fragments[4]);

			if (traceId == null || spanId == null || rootSpanId == null || index == null) {
				logger.warn(`Missing URL fragments for east pane (${itemType})`, fragments);
				return null;
			}

			return { type: "span-event", traceId, primarySpanId: rootSpanId, spanId, index };
		}

		case "component": {
			/**
			 * The fragment has `type:value` format, but the value itself might include `:`,
			 * so joining the tail restores the value.
			 */
			const value = fragments.slice(1).join(RIGHT_PANE_ITEM_FRAGMENTS_SEPARATOR);
			const numberId = decodeNumber(value);

			if (numberId != null && !isNaN(numberId)) {
				return { type: "component", id: numberId };
			}

			const stringId = decodeString(value);

			if (stringId != null) {
				return { type: "component", id: stringId };
			}

			return null;
		}

		default: {
			const rawId = decodeNumber(fragments[1]);

			if (rawId == null || isNaN(rawId)) {
				return null;
			}

			return { type: itemType, id: rawId };
		}
	}
};

const decodeRightPane = (input: undefined | null | string | Array<null | string>): RightPaneState => {
	const result = decodeObject(input, RIGHT_PANE_OBJECT_KEY_VALUE_SEPARATOR, RIGHT_PANE_OBJECT_ENTRY_SEPARATOR);

	const hidden = decodeBoolean(result?.hidden) ?? RightPaneState.defaults.hidden;
	const chain =
		decodeDelimitedArray(result?.chain, RIGHT_PANE_ITEMS_SEPARATOR)?.map(decodeRightPaneItem).filter(isPresent) ??
		RightPaneState.defaults.chain;
	const activeTab = decodeRightPaneActiveTab(result?.active_tab) ?? RightPaneState.defaults.activeTab;

	return RightPaneState.normalize({ hidden, chain, activeTab });
};

/**
 * R I G H T   P A N E   S T A T E
 */

export type RightPaneActiveTab = "pinned-items" | "selection";

export type RightPaneComponentItemType = "component";

export type RightPaneGroupItemType = "group";

export type RightPaneRelationItemType = "relation" | "indirect-relation" | "group-relation";

export type RightPaneEventItemType = "event";

export type RightPaneEventsItemType = "events";

export type RightPaneSpanItemType = "span";
export type RightPaneSpanEventItemType = "span-event";

interface RightPaneItemSpan {
	// a row in the traces table is uniquely identified by traceId and rootSpanId
	traceId: string;
	primarySpanId: string;
	spanId: string;
}

interface RightPaneItemSpanEvent extends RightPaneItemSpan {
	index: number;
}

export type RightPaneItem =
	| {
			type: RightPaneComponentItemType;
			id: number | string;
	  }
	| {
			type: RightPaneRelationItemType | RightPaneGroupItemType;
			id: number;
	  }
	| {
			type: RightPaneEventItemType;
			id: string;
			// itemTime is needed because events should be fetched from a consistent time
			itemTime: number;
	  }
	| {
			type: RightPaneEventsItemType;
			startTime: Time;
			endTime: Time;
			eventType?: string;
	  }
	| ({ type: RightPaneSpanItemType } & RightPaneItemSpan)
	| ({ type: RightPaneSpanEventItemType } & RightPaneItemSpanEvent);

const queryParam: QueryParamConfig<undefined | RightPaneState, RightPaneState> = {
	decode: decodeRightPane,
	encode: encodeRightPane,
	equals: isDeepEqual,
};

/**
 * This state is managed by URL and does not exist in Redux state
 *
 * @param hidden pane could be open or closed, but always exists on the page
 * @param activeTab indicates which tab is active by index starting from 0
 * @param chain holds 100 long history of RightPane transitions from element to element
 */
export abstract class RightPaneState {
	abstract readonly hidden: boolean;
	abstract readonly activeTab: RightPaneActiveTab;
	abstract readonly chain: Array<RightPaneItem>;

	static readonly defaults: RightPaneState = {
		hidden: true,
		activeTab: "pinned-items",
		chain: [],
	};

	static readonly searchParams = {
		rightPane: queryParam,
	};

	static isDefaults(rightPane: RightPaneState): boolean {
		return isDeepEqual(RightPaneState.defaults, rightPane);
	}

	static normalize(rightPane: RightPaneState): RightPaneState {
		const chain =
			rightPane.chain.length > RIGHT_PANE_MAX_CHAIN_SIZE
				? rightPane.chain.slice(-RIGHT_PANE_MAX_CHAIN_SIZE)
				: rightPane.chain;
		const activeTab = rightPane.activeTab === "selection" && chain.length === 0 ? "pinned-items" : rightPane.activeTab;

		if (chain === rightPane.chain && activeTab === rightPane.activeTab) {
			return rightPane;
		}

		return {
			hidden: rightPane.hidden,
			chain,
			activeTab,
		};
	}

	static getCurrentItem(rightPane: RightPaneState): null | RightPaneItem {
		return rightPane.chain[rightPane.chain.length - 1] ?? null;
	}

	static popItem(rightPane: RightPaneState): RightPaneState {
		if (rightPane.chain.length === 0) {
			return RightPaneState.normalize(rightPane);
		}

		return RightPaneState.normalize({
			...rightPane,
			chain: rightPane.chain.slice(0, -1),
		});
	}

	static pushItem(item: RightPaneItem, rightPane: RightPaneState): RightPaneState {
		// don't push the same item twice in a row
		if (isDeepEqual(RightPaneState.getCurrentItem(rightPane), item)) {
			return rightPane;
		}

		return RightPaneState.normalize({
			...rightPane,
			chain: [...rightPane.chain, item],
		});
	}

	static switchTab(activeTab: RightPaneActiveTab, rightPane: RightPaneState): RightPaneState {
		if (activeTab === rightPane.activeTab) {
			return rightPane;
		}

		return RightPaneState.normalize({ ...rightPane, activeTab });
	}

	/**
	 * Use when you want to manipulate the 'rightPane' search parameter, when leaving/going to a view or explore subview
	 * If right pane is visible, it will automatically close all tabs except the 'Pinned items' tab
	 */
	static resetWhenVisible(rightPane: RightPaneState): RightPaneState {
		return rightPane.hidden ? rightPane : { ...RightPaneState.defaults, hidden: false };
	}
}
